import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { numberMessage, registrationRule, vatRule, type NumberLanguage } from '@smbsuite/company-numbers';
// The dictionaries, the language list and the interpolation are shared with the mobile app (libs/i18n,
// TF-269); this file is the web app's provider around them.
import { choosableLanguages, en, formatBytes, interpolate, isLanguage, languageEntry, preferredLanguage, type Dictionary, type Language, type LanguageEntry, type MessageKey } from '@smbsuite/i18n';
import { useViewerZone } from '../time/viewer-zone';

export { formatBytes, interpolate };
export { LANGUAGES, backendLanguage, languageEntry, type Language, type LanguageEntry } from '@smbsuite/i18n';

const STORAGE_KEY = 'teamfolio.language';
// Languages not yet released are offered only to reviewers (TF-183): opening the app once with
// ?languages=review switches it on in that browser, ?languages=released off again.
const REVIEW_KEY = 'teamfolio.reviewLanguages';

function stored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null; // storage blocked
  }
}
function store(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // not remembered, still applied
  }
}

/** Whether this browser is set to review languages that are not released yet. */
export function reviewingLanguages(): boolean {
  const asked = typeof location === 'undefined' ? null : new URLSearchParams(location.search).get('languages');
  if (asked === 'review') store(REVIEW_KEY, 'on');
  if (asked === 'released') store(REVIEW_KEY, null);
  return stored(REVIEW_KEY) === 'on';
}

/** The languages this browser may choose from, by their own names (released ones, or all for a reviewer). */
export const choosable = (): LanguageEntry[] => choosableLanguages(reviewingLanguages());

/** The language to open in: the one chosen before (if still offered), else the browser's preferred ones. */
export function initialLanguage(): Language {
  const saved = stored(STORAGE_KEY);
  if (isLanguage(saved) && choosable().some((entry) => entry.code === saved)) return saved;
  return preferredLanguage(typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language]), reviewingLanguages());
}

// The dictionaries downloaded so far; English is always here, as the words of last resort.
const dictionaries = new Map<Language, Dictionary>([['en', en]]);
const loading = new Map<Language, Promise<Dictionary>>();

/** A language's dictionary, downloaded once (only the chosen language's is ever fetched). */
export function loadLanguage(language: Language): Promise<Dictionary> {
  const ready = dictionaries.get(language);
  if (ready) return Promise.resolve(ready);
  let pending = loading.get(language);
  if (!pending) {
    pending = languageEntry(language)
      .load()
      .then((dictionary) => {
        dictionaries.set(language, dictionary);
        return dictionary;
      })
      .finally(() => loading.delete(language));
    loading.set(language, pending);
  }
  return pending;
}

export interface I18n {
  language: Language;
  /** The language's entry: its tags for dates and numbers, what the back end writes in. */
  entry: LanguageEntry;
  setLanguage: (language: Language) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
  /** Plural: picks key.zero / key.one / key.other by the language's rules. */
  tp: (key: string, count: number, vars?: Record<string, string | number>) => string;
  date: (value: string | Date, style?: 'short' | 'long') => string;
  bytes: (value: number) => string;
  number: (value: number) => string;
  /** Text order in the language (Intl.Collator, TF-190): Swedish puts å, ä, ö after z, Estonian š, z, ž after s. */
  compare: (a: string, b: string) => number;
}

const I18nContext = createContext<I18n | null>(null);

/**
 * The words for an answer the API gives in its own code rather than in prose (TF-113), in the language
 * shown: errorMessage() asks here, outside React. Kept current by the provider below.
 */
let current: I18n | null = null;

/** The language the company-number rules speak (TF-231): Latvian, or English for the rest. */
export const numberLanguage = (language: string): NumberLanguage => (language === 'lv' ? 'lv' : 'en');

export function apiErrorText(code: string, details: Record<string, unknown>): string | null {
  if (!current) return null;
  if (code === 'READ_ONLY') {
    if (details.reason === 'member_limit') return current.tp('memberLimit.refused', typeof details.limit === 'number' ? details.limit : 0);
    const until = typeof details.until === 'string' ? current.date(details.until) : null;
    return until ? current.t('readOnly.refused', { date: until }) : current.t('readOnly.refusedNoDate');
  }
  // An address that bounced or complained (TF-210): said at the address field, in words to act on.
  if (code === 'ADDRESS_BLOCKED') return current.t(details.reason === 'failed' ? 'mail.blockedFailed' : 'mail.blockedComplained');
  // The limits (TF-112): registering a company, invitations and shares.
  const allowed = typeof details.allowed === 'number' ? details.allowed : 0;
  if (code === 'COMPANY_LIMIT') return current.tp(details.limit === 'day' ? 'bots.companiesPerDay' : 'bots.companiesInAll', allowed);
  if (code === 'MAIL_LIMIT') return current.tp('bots.mailLimit', allowed);
  // Teams (TF-296): the plan's limit.
  if (code === 'TEAM_LIMIT') return current.tp('teams.limitReached', typeof details.limit === 'number' ? details.limit : 0);
  // TF-298: a team that is some records' only one is not deleted before they move.
  if (code === 'TEAM_HAS_RECORDS') return current.t('teams.hasRecords', { count: typeof details.records === 'number' ? details.records : 0 });
  // A company number that does not fit its country's rule (TF-231): what was expected, with an example.
  if (code === 'NUMBER_FORMAT' && (details.kind === 'registration' || details.kind === 'vat')) {
    const country = typeof details.country === 'string' ? details.country : null;
    const rule = details.kind === 'registration' ? registrationRule(country) : vatRule(country);
    return numberMessage(details.kind, country, { ok: false, problem: details.problem === 'prefix' ? 'prefix' : 'format', rule }, numberLanguage(current.language));
  }
  const key = KNOWN_ERRORS[code];
  return key ? current.t(key) : null;
}

/** Answers the app has its own words for, by code: the bot check and sign-up (TF-112), and none at all (TF-133). */
const KNOWN_ERRORS: Record<string, MessageKey> = {
  TURNSTILE_FAILED: 'bots.failed',
  TURNSTILE_UNAVAILABLE: 'bots.unavailable',
  TURNSTILE_MISCONFIGURED: 'bots.ourFault',
  NOT_SET_UP: 'bots.notSetUp',
  SIGNUP_CLOSED: 'bots.signupClosed',
  TOO_MANY_SIGNUPS: 'bots.tooManySignups',
  DISPOSABLE_EMAIL: 'bots.disposable',
  DOMAIN_BLOCKED: 'bots.domainBlocked',
  // Mail logging from Microsoft 365 switched off for the company (TF-323).
  MAIL_LOG_OFF: 'mailLog.ms.off',
  // Teams (TF-296).
  TEAM_NAME_TAKEN: 'teams.nameTaken',
  LAST_TEAM_ADMIN: 'teams.lastAdmin',
  TEAM_ROLE_NOT_ALLOWED: 'teams.roleNotAllowed',
  ALL_COMPANY_TEAM: 'teams.allCompanyFixed',
  NOT_IN_TEAM: 'teams.notInTeam',
  TEAM_READ_ONLY: 'crm.teamReadOnly',
  LAST_TEAM: 'crm.lastTeam',
  RECORD_READ_ONLY: 'crm.recordReadOnly',
  RECORD_SENSITIVE: 'crm.recordSensitive',
  TEAM_HIDDEN_INVITE_ONLY: 'teams.hiddenInviteOnly',
  RECORDS_UNKNOWN: 'teams.recordsUnknown',
  TEAM_INHERITED: 'files.teamInheritedError',
  // Sensitive tags (TF-357).
  TAG_SENSITIVE: 'tags.removeHint',
  OWNER_ONLY: 'tags.ownersOnly',
  PLAN_FEATURE: 'tags.planNeeded',
  // Sensitive tags on documents (TF-359).
  DOCUMENT_SENSITIVE: 'files.documentSensitive',
  SHARE_EXTERNAL_SENSITIVE: 'share.externalSensitive',
  // The form for reporting a public link (TF-256).
  NOT_A_LINK: 'report.notALink',
  LINK_UNKNOWN: 'report.linkUnknown',
  TOO_MANY_REPORTS: 'report.tooMany',
  EMAIL_REQUIRED: 'report.emailRequired',
  TOO_MANY_CHECKS: 'bots.tooManyChecks',
  // Registering a company without its number (TF-231).
  REGISTRATION_NO_REQUIRED: 'company.registrationRequired',
  // A company from a country that cannot be served, and one held while it is checked (TF-213).
  COUNTRY_NOT_SERVED: 'company.countryNotServed',
  COMPANY_HELD: 'company.heldNote',
  // A request that never reached the platform (TF-133).
  UNREACHABLE: 'status.unreachableShort',
};

/**
 * The app's language. `language` given (the design gallery), it is that one, fixed: nothing is
 * remembered and the page's own language is left alone. Otherwise the person's choice: the page's
 * <html lang> follows it, and a language is shown once its dictionary is here (main.tsx fetches the
 * first before the app draws, so nobody sees English flash past).
 */
export function I18nProvider({ children, language: fixed }: { children: ReactNode; language?: Language }) {
  const [chosen, setChosen] = useState<Language>(initialLanguage);
  const wanted = fixed ?? chosen;
  // What is shown: the wanted language once its dictionary has arrived, English until then.
  const [shown, setShown] = useState<Language>(() => (dictionaries.has(wanted) ? wanted : 'en'));

  useEffect(() => {
    let current = true;
    if (dictionaries.has(wanted)) setShown(wanted);
    else void loadLanguage(wanted).then(() => current && setShown(wanted), () => current && setShown('en'));
    return () => {
      current = false;
    };
  }, [wanted]);

  useEffect(() => {
    if (!fixed) document.documentElement.lang = shown;
  }, [fixed, shown]);

  const setLanguage = useCallback((next: Language) => {
    setChosen(next);
    store(STORAGE_KEY, next);
  }, []);

  const language = shown;
  // Dates and times in the viewer's time zone (TF-220): the one fixed in My account, else the device's.
  const zone = useViewerZone();
  const value = useMemo<I18n>(() => {
    const entry = languageEntry(language);
    const dictionary: Dictionary = dictionaries.get(language) ?? en;
    const plural = new Intl.PluralRules(entry.intl);
    const number = new Intl.NumberFormat(entry.intl);
    const collator = new Intl.Collator(entry.intl);
    const dates = {
      short: new Intl.DateTimeFormat(entry.intl, { dateStyle: 'medium', timeZone: zone }),
      long: new Intl.DateTimeFormat(entry.intl, { dateStyle: 'medium', timeStyle: 'short', timeZone: zone }),
    };
    const reference: Dictionary = en;
    const lookup = (key: string): string => dictionary[key] ?? reference[key] ?? key;
    return {
      language,
      entry,
      setLanguage,
      t: (key, vars) => interpolate(lookup(key), vars),
      tp: (key, count, vars) => {
        const form = plural.select(count);
        const template = dictionary[`${key}.${form}`] ?? dictionary[`${key}.other`] ?? lookup(`${key}.other`);
        return interpolate(template, { count: number.format(count), ...vars });
      },
      date: (value, style = 'short') => dates[style].format(typeof value === 'string' ? new Date(value) : value),
      bytes: (value) => formatBytes(value, entry.intl),
      number: (value) => number.format(value),
      compare: collator.compare,
    };
  }, [language, setLanguage, zone]);

  if (!fixed) current = value;
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const i18n = useContext(I18nContext);
  if (!i18n) throw new Error('useI18n outside I18nProvider');
  return i18n;
}
